import { FC, useEffect, useMemo, useRef, useState } from 'react';
import Image, { ImageProps } from 'components/commercetools-ui/atoms/image';
import { useSliderModal } from 'components/commercetools-ui/atoms/slider/hooks/useSliderModal';
import SliderModal from 'components/commercetools-ui/atoms/slider/slider-modal';
import { classnames } from 'helpers/utils/classnames';
import SwipeGallery from '.';

/**
 * The PDP's media gallery: a major image, two medium tiles and an image drawer.
 *
 * Desktop layout and roles:
 * - Major: the parent's lifestyle photos. It opens on the first and never changes on a variant or
 *   finish change or a drawer click; a tray of thumbnails in its corner lets the shopper switch
 *   between lifestyle photos.
 * - Medium A (left): the viewer. It shows whatever the user picks in the drawer. It opens on the
 *   video when one exists (rule 1), otherwise on the first image for the selected finish.
 * - Medium B (right): the finish swatch with a chip overlay (products with finishes), otherwise the
 *   next image.
 * - Drawer: every viewable item — video first, then images — so anything can be brought into
 *   Medium A. Past DRAWER_COLLAPSED it ends in a "+N more" tile that expands it.
 *
 * Performance budget — only the major image is allowed to be heavy:
 * - The mosaic is server-rendered for every viewport and CSS (`lg:`) decides what shows, so desktop
 *   never renders, downloads or hydrates the swipe gallery first.
 * - The major image is the only eager request (deliberately not fetchPriority="high"; see below).
 *   Its `sizes` match the swipe gallery's below desktop, so on phones both resolve to the same URL
 *   and the swap is a cache hit.
 * - Medium tiles and drawer thumbnails are lazy + low priority and sized to their slots (~360px and
 *   ~90px). Below desktop they are display:none and lazy, so phones never request them.
 * - The video tile is a still poster; the <video> element only exists after the viewer clicks play.
 * - The collapsed drawer renders only DRAWER_COLLAPSED thumbnails; the rest aren't in the DOM.
 * - Below desktop the swipe gallery renders instead; it adds no download on desktop, since Swiper
 *   ships in the PDP bundle for the related-products sliders regardless.
 * - Every slot has a fixed aspect ratio, and below desktop a placeholder holds the swipe gallery's
 *   thumbnail-strip height, so nothing shifts as images arrive or when the swipe gallery takes over.
 *
 * Video: a file or a YouTube video. On phones it is the second slide, after the lead lifestyle photo.
 */
/** A product video: a file (`src`) or a YouTube video (`youtubeId`), shown as a poster until played. */
export type MosaicVideo = { title: string; duration?: string; src?: string; youtubeId?: string; poster?: ImageProps };

/**
 * The player, mounted only after a click so a PDP never loads YouTube's player up front.
 * youtube-nocookie keeps YouTube from setting cookies until the shopper actually plays.
 */
const VideoPlayer: FC<{ video: MosaicVideo }> = ({ video }) =>
  video.youtubeId ? (
    <iframe
      src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?autoplay=1&rel=0&playsinline=1`}
      title={video.title}
      className="absolute inset-0 size-full border-0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  ) : (
    // eslint-disable-next-line jsx-a11y/media-has-caption -- captions come with real video data
    <video src={video.src} className="size-full object-cover" controls autoPlay playsInline />
  );

interface GalleryMosaicProps {
  /**
   * The server's device guess. Phones (most traffic) then get the carousel in the server HTML instead
   * of hydrating the desktop mosaic and swapping it out right as they start scrolling. The breakpoint
   * check after mount still corrects a wrong guess either way.
   */
  initialPhoneLayout?: boolean;
  /** The parent's lifestyle photos: the major image shows one, the tray switches between them. */
  lifestyles?: Array<ImageProps>;
  /**
   * The full ordered gallery (finish-specific first), the same list the swipe gallery takes. The
   * lifestyle and swatch images are left out of the viewer and drawer because they have their own
   * slots; `initialSlide` and `onActiveIndexChange` speak in indexes of this full list, so the
   * PDP's remember-by-tier logic works unchanged.
   */
  images: Array<ImageProps>;
  /** Where Medium A should open, as an index into `images`. Re-read whenever the image set changes. */
  initialSlide?: number;
  /** Fires only when the viewer picks an image in the drawer, with its index in `images`. */
  onActiveIndexChange?: (index: number) => void;
  video?: MosaicVideo;
  /**
   * The selected finish's swatch (products with finishes only). Shows the species/finish image when
   * one is tagged, otherwise a square of the finish's hex. Its chip overlay drives the same finish
   * change as the buy-box picker, so the two always agree.
   */
  swatch?: {
    image?: ImageProps;
    hex?: string;
    name?: string;
    options: Array<{ name: string; hex: string | null }>;
    onSelect: (name: string) => void;
    /**
     * The other options' photos when the swatch shows product shots (a color sold as separate SKUs
     * with no finish photo): kept out of the viewer too, so a color's shot only appears in this tile.
     */
    siblingImages?: ImageProps[];
  };
}

/**
 * A finish with no swatch photo is drawn as a flat square of its hex, built in the page as an SVG
 * data URI, so it can be a carousel slide like any photo without a network request.
 */
const hexSwatchImage = (hex: string, name?: string): ImageProps => ({
  src: `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><rect width="1" height="1" fill="${hex}"/></svg>`,
  )}`,
  alt: name ? `${name} finish` : '',
});

/** What Medium A is showing: the video, or an index into the viewer's image list. */
type ViewerItem = 'video' | number;

// 7 thumbnails + the "+N more" tile fill one row of 8.
const DRAWER_COLLAPSED = 7;
// Must match Tailwind's `lg` screen (tailwind.config.js) so JS and CSS agree on the layout.
const DESKTOP_QUERY = '(min-width: 1025px)';
// Lifestyle photos are mostly landscape, but every slot they fill is square. Asked for by width
// alone (c_limit), a landscape photo comes back short of the slot and the browser upscales it to
// cover — visibly soft (Joel, 6 Oct 2026). Cropping to 1:1 in Cloudinary (c_fill, g_auto) returns
// a square at full resolution instead. The enlarge modal still shows the whole, uncropped photo.
const SQUARE = '1:1';

// Below desktop these mirror the swipe gallery's sizes so both pick the same srcset candidate.
const MAJOR_SIZES = '(max-width: 743px) 100vw, (max-width: 1023px) 48vw, 740px';
const MEDIUM_SIZES = '360px';
const THUMB_SIZES = '90px';
const PHONE_SWATCH_SIZES = '250px';
const TRAY_SIZES = '56px';
// Six thumbnails fit along the major image's bottom edge without wrapping onto the photo.
const LIFESTYLE_TRAY_MAX = 6;
// Finish chips on the swatch tile: one row only. The tile is a shortcut, not the finish control —
// the buy box's Finish picker (`[data-finish-picker]`) has every option with names and prices — so
// past this many the row ends in a "+N" chip that jumps to the picker. The selected finish is always
// in the row (it displaces the last visible chip), so the tile never shows a selection it lacks.
// Desktop chips are 28px with hover titles, so seven read fine. Phone chips are 40px with a 12px
// gap (a 52px tap pitch; the finishes are near-identical tans, so 32/8 was too tight to tell apart
// or tap reliably — Joel, 4 Oct 2026); five plus the overflow chip is 300px, which fits a 375px
// screen inside the 12px inset. Seven at that size would not.
const SWATCH_CHIP_MAX = 7;
const SWATCH_CHIP_MAX_PHONE = 5;
const visibleSwatchOptions = <T extends { name: string }>(
  options: T[],
  selectedName?: string,
  max = SWATCH_CHIP_MAX,
) => {
  if (options.length <= max) return { visible: options, hidden: 0 };
  const visible = options.slice(0, max);
  const selected = options.find((option) => option.name === selectedName);
  if (selected && !visible.includes(selected)) visible[max - 1] = selected;
  return { visible, hidden: options.length - max };
};
const jumpToFinishPicker = () => {
  const picker = document.querySelector<HTMLElement>('[data-finish-picker]');
  if (!picker) return;
  picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
  picker.querySelector<HTMLElement>('button, [tabindex]')?.focus({ preventScroll: true });
};

const PlayIcon: FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M8 5v14l11-7z" fill="currentColor" />
  </svg>
);

// The video's call to action. Solid white with a dark label so it reads at a glance on any poster —
// a translucent dark circle disappeared against dark stills on phones (Joel, 6 Oct 2026).
const WatchVideoPill: FC<{ duration?: string }> = ({ duration }) => (
  <span className="flex items-center gap-8 rounded-full bg-white py-12 pl-16 pr-20 text-16 font-medium text-primary-black transition-transform group-hover:scale-105">
    <PlayIcon size={22} />
    Watch video
    {duration && <span className="font-normal text-secondary-black">{duration}</span>}
  </span>
);

// Thumbnail marker for the video: a solid white disc with a dark play glyph.
const PlayBadge: FC = () => (
  <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
    <span className="flex size-32 items-center justify-center rounded-full bg-white pl-2 text-primary-black">
      <PlayIcon size={18} />
    </span>
  </span>
);

const imageKey = (image?: ImageProps) => image?.media?.mediaId ?? image?.src;

const GalleryMosaic: FC<GalleryMosaicProps> = ({
  initialPhoneLayout,
  lifestyles = [],
  images,
  initialSlide = 0,
  onActiveIndexChange,
  video,
  swatch,
}) => {
  const [lifestyleIndex, setLifestyleIndex] = useState(0);
  const lifestyle = lifestyles[Math.min(lifestyleIndex, lifestyles.length - 1)];
  // The viewer/drawer list: the full gallery minus the images that have their own slots — every
  // lifestyle photo (the major/tray on desktop, the leading slides on phones) and the swatch — so a
  // photo tagged both lifestyle and product never shows twice.
  const ownSlotKeys = new Set([...lifestyles, ...(swatch?.siblingImages ?? [])].map(imageKey));
  const viewerToFull = images
    .map((_, index) => index)
    .filter((index) => {
      const key = imageKey(images[index]);
      return !ownSlotKeys.has(key) && key !== imageKey(swatch?.image);
    });
  const viewerImages = viewerToFull.map((index) => images[index]);
  // A remembered full-list index lands on that image, or the next viewable one (the closest).
  const toViewerIndex = (fullIndex: number) => {
    const next = viewerToFull.findIndex((index) => index >= fullIndex);
    return next === -1 ? Math.max(viewerToFull.length - 1, 0) : next;
  };
  const openingItem = (): ViewerItem => (video ? 'video' : toViewerIndex(initialSlide));

  const [viewerItem, setViewerItem] = useState<ViewerItem>(openingItem);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  // Below desktop the swipe gallery takes over once the page is running. Until then the
  // server-rendered major image (and the strip placeholder below it) holds the layout, so the swap
  // never shifts the page. The swipe gallery ships in the PDP bundle anyway (Swiper also powers the
  // related-products sliders), so it is imported directly — loading it on demand saved nothing and
  // delayed the phone handoff by a network round trip.
  const [isBelowDesktop, setIsBelowDesktop] = useState(initialPhoneLayout ?? false);
  const enlarge = useSliderModal(true);

  // A new image set (finish or variant change) reopens the viewer on its first item. The mosaic
  // itself stays mounted, so the phone carousel keeps the slide the shopper is on.
  const imageSetKey = images.map(imageKey).join('|');
  useEffect(() => {
    setViewerItem(openingItem());
    setDrawerOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when the image set changes
  }, [imageSetKey]);

  // A layout swap, or a remounted phone carousel (new product images), would otherwise mount the
  // player again with autoplay, off-screen; both start from the poster instead. On desktop the video
  // tile stays put through a finish or variant change, so it keeps playing.
  const viewerSetKey = viewerImages.map(imageKey).join('|');
  useEffect(() => {
    setVideoPlaying(false);
  }, [isBelowDesktop]);
  useEffect(() => {
    if (isBelowDesktop) setVideoPlaying(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only a new image set remounts the carousel
  }, [viewerSetKey]);

  useEffect(() => {
    const query = window.matchMedia(DESKTOP_QUERY);
    const update = () => setIsBelowDesktop(!query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  // The finish label and chips, shared by the desktop swatch tile and the phone swatch slide so
  // the two can never drift apart. On phones the chips sit under the label at the top, clear of
  // the carousel's pagination dots; on desktop (no dots) they sit at the bottom of the tile.
  // `swiper-no-swiping` keeps chip taps from swiping the carousel, and stopping the click keeps
  // them from opening the enlarge-image modal.
  const renderSwatchOverlay = () =>
    swatch && (
      <>
        <span className="absolute left-12 top-12 bg-white/90 px-12 py-4 text-12 text-primary-black">
          Finish: <span className="font-medium">{swatch.name}</span>
        </span>
        {swatch.options.length > 1 &&
          (() => {
            const { visible, hidden } = visibleSwatchOptions(
              swatch.options,
              swatch.name,
              isBelowDesktop ? SWATCH_CHIP_MAX_PHONE : SWATCH_CHIP_MAX,
            );
            return (
              <div
                role="group"
                aria-label="Change finish"
                // eslint-disable-next-line tailwindcss/no-custom-classname -- Swiper's built-in no-swipe marker
                className="swiper-no-swiping absolute left-12 top-48 z-10 flex flex-nowrap gap-12 lg:bottom-12 lg:top-auto lg:gap-8"
                onClick={(event) => event.stopPropagation()}
              >
                {visible.map((option) => {
                  const selected = option.name === swatch.name;
                  return (
                    <button
                      key={option.name}
                      type="button"
                      title={option.name}
                      aria-label={option.name}
                      aria-pressed={selected}
                      onClick={() => swatch.onSelect(option.name)}
                      style={{ backgroundColor: option.hex ?? undefined }}
                      // 40px on touch screens (comfortable tap target), 28px on desktop.
                      className={classnames(
                        'size-40 border-2 border-white transition lg:size-28',
                        selected ? 'ring-2 ring-[#212121]' : 'hover:ring-2 hover:ring-white/70',
                      )}
                    />
                  );
                })}
                {hidden > 0 && (
                  <button
                    type="button"
                    title={`${hidden} more finishes`}
                    aria-label={`${hidden} more finishes, go to the finish picker`}
                    onClick={jumpToFinishPicker}
                    className="size-40 border-2 border-white bg-white/90 text-12 font-medium text-primary-black transition hover:ring-2 hover:ring-white/70 lg:size-28"
                  >
                    +{hidden}
                  </button>
                )}
              </div>
            );
          })()}
      </>
    );

  // Lifestyle tray (desktop only): thumbnails over the major image's bottom corner, like the swatch
  // chips. Phones skip it and open the carousel on the first lifestyle photo. Thumbnails are tiny,
  // lazy and low priority so they never compete with the major image itself.
  const renderLifestyleTray = () =>
    lifestyles.length > 1 && (
      <div
        role="group"
        aria-label="Lifestyle photos"
        className="absolute bottom-12 left-12 z-10 hidden flex-wrap gap-8 lg:flex"
      >
        {lifestyles.slice(0, LIFESTYLE_TRAY_MAX).map((photo, index) => {
          const selected = index === lifestyleIndex;
          return (
            <button
              key={imageKey(photo) ?? index}
              type="button"
              aria-label={`Show lifestyle photo ${index + 1} of ${lifestyles.length}`}
              aria-pressed={selected}
              onClick={() => setLifestyleIndex(index)}
              className={classnames(
                'relative size-56 overflow-hidden border-2 border-white bg-white transition',
                selected ? 'ring-2 ring-[#212121]' : 'hover:ring-2 hover:ring-white/70',
              )}
            >
              <Image
                src={photo.src}
                alt=""
                media={photo.media}
                cloudinaryPreset="thumbnail"
                fill
                style={{ objectFit: 'cover' }}
                sizes={TRAY_SIZES}
                loading="lazy"
                fetchPriority="low"
              />
            </button>
          );
        })}
      </div>
    );

  // On phones the swatch slide is full width, but a grain texture holds up at 750px (~65 KB) where
  // a full-width photo would fetch 1200px (~157 KB). 250px × 3 (phone density) = the 750px candidate.
  const swatchSlideImage = swatch?.image
    ? { ...swatch.image, sizes: PHONE_SWATCH_SIZES }
    : swatch?.hex
      ? hexSwatchImage(swatch.hex, swatch.name)
      : undefined;
  // Phones get the same content as the desktop tiles, in tile order: lifestyle (major), the
  // viewer's opening image (medium A), the swatch (medium B), then the rest of the lifestyle
  // photos (the desktop tray has no phone equivalent, and shoppers want them — Joel, 4 Oct 2026),
  // then the rest of the drawer.
  // Each slide's viewer index (null for lifestyle and swatch slides), so the carousel can report and
  // reopen at a product image the way the classic gallery does.
  // Built once per set of photos: the carousel memoizes on its slide objects, so fresh copies on
  // every render (a swatch tap, a layout update) would defeat it.
  const squareLifestyles = useMemo(
    () => lifestyles.slice(0, LIFESTYLE_TRAY_MAX).map((image) => ({ ...image, ratio: SQUARE })),
    [lifestyles],
  );
  // The video slide sits where Medium A opens on desktop (a product with video opens on it there).
  // It needs its own poster: borrowing the first product image would give two slides the same image
  // object (and key), so a video without one stays desktop-only.
  const videoSlideImage = video?.poster;
  const swipeSlides = [
    { image: squareLifestyles[0], viewer: null },
    { image: videoSlideImage, viewer: null },
    { image: viewerImages[0], viewer: 0 },
    { image: swatchSlideImage, viewer: null },
    ...squareLifestyles.slice(1).map((image) => ({ image, viewer: null })),
    ...viewerImages.slice(1).map((image, index) => ({ image, viewer: index + 1 })),
  ].filter((slide): slide is { image: ImageProps; viewer: number | null } => Boolean(slide.image));
  const swipeImages = swipeSlides.map((slide) => slide.image);
  const swatchSlideIndex = swatchSlideImage ? swipeImages.indexOf(swatchSlideImage) : -1;
  const videoSlideIndex = videoSlideImage ? swipeImages.indexOf(videoSlideImage) : -1;
  // Over the poster: a play button, then the player itself. Stopping the click keeps a tap from
  // opening the enlarge-image modal. The overlay covers the slide, so it must not carry
  // `swiper-no-swiping` (that would trap the shopper on this slide); Swiper already drops the
  // click at the end of a swipe, so swiping across the poster doesn't start the video.
  const renderVideoSlideOverlay = () =>
    video && (
      <div className="absolute inset-0 z-10 bg-black/20" onClick={(event) => event.stopPropagation()}>
        {videoPlaying ? (
          // Letterboxed to 16:9 in the square slide: a cross-origin iframe swallows every touch, so the
          // bands above and below it are where the shopper can still swipe on to the next slide.
          <div className="absolute inset-x-0 top-1/2 aspect-video -translate-y-1/2 bg-black">
            <VideoPlayer video={video} />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setVideoPlaying(true)}
            className="group flex size-full items-center justify-center"
            aria-label={`Play video: ${video.title}`}
          >
            <WatchVideoPill duration={video.duration} />
            <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-12 py-32 text-left text-14 font-medium text-white">
              {video.title}
            </span>
          </button>
        )}
      </div>
    );
  // A new set of product images remounts the carousel (as the classic gallery does) on the remembered
  // product image; a shopper on a lifestyle or swatch slide stays on it, since those don't change.
  const phoneSlide = useRef(0);
  const openingSwipeIndex = () => {
    if (swipeSlides[phoneSlide.current]?.viewer === null) return Math.min(phoneSlide.current, swipeSlides.length - 1);
    const at = swipeSlides.findIndex((slide) => slide.viewer === toViewerIndex(initialSlide));
    return at === -1 ? 0 : at;
  };

  if (isBelowDesktop) {
    return (
      <SwipeGallery
        // Keyed on the product images, not the swatch: a chip tap updates the swatch slide in place
        // without rebuilding Swiper or moving the shopper off the slide they're on.
        key={viewerSetKey}
        images={swipeImages}
        initialSlide={openingSwipeIndex()}
        onActiveIndexChange={(index) => {
          phoneSlide.current = index;
          // Swiping off the video stops it: the player unmounts with the poster back in its place.
          if (index !== videoSlideIndex) setVideoPlaying(false);
          const viewer = swipeSlides[index]?.viewer;
          if (viewer !== null && viewer !== undefined) onActiveIndexChange?.(viewerToFull[viewer]);
        }}
        enableImageModal
        slideOverlays={{
          ...(swatchSlideIndex >= 0 ? { [swatchSlideIndex]: renderSwatchOverlay() } : {}),
          ...(videoSlideIndex >= 0 ? { [videoSlideIndex]: renderVideoSlideOverlay() } : {}),
        }}
        thumbnailOverlays={
          videoSlideIndex >= 0
            ? {
                [videoSlideIndex]: <PlayBadge />,
              }
            : undefined
        }
      />
    );
  }

  const major = lifestyle ?? viewerImages[0];
  // Clamped during render: the reset effect runs after a new image set has already rendered once,
  // and an out-of-range index would drop Medium A for that frame.
  const viewerIndex =
    typeof viewerItem === 'number' ? Math.min(viewerItem, Math.max(viewerImages.length - 1, 0)) : null;
  const viewerImage = viewerIndex !== null ? viewerImages[viewerIndex] : undefined;
  const hasViewer = Boolean(video) || viewerImages.length > 0;
  // Without a swatch, Medium B shows the image after whatever Medium A is on, so the two never match.
  const secondaryIndex =
    !swatch && viewerImages.length > 1
      ? video || viewerIndex === null
        ? 0
        : (viewerIndex + 1) % viewerImages.length
      : null;

  // Desktop click-to-enlarge, the same modal the classic gallery opens: lifestyle photos first,
  // then every viewer image, so the arrows walk the whole set from whichever tile was clicked.
  const enlargeImages = [...lifestyles.slice(0, LIFESTYLE_TRAY_MAX), ...viewerImages].map(({ src, media, alt }) => ({
    src,
    media,
    alt,
  }));
  const openEnlarged = (index: number, trigger: HTMLElement) =>
    enlargeImages[index] && enlarge.handleSlideClick(enlargeImages[index], trigger, enlargeImages, index);
  const lifestyleCount = Math.min(lifestyles.length, LIFESTYLE_TRAY_MAX);
  const enlargeButton = (index: number) => (
    <button
      type="button"
      aria-label="View enlarged image"
      onClick={(event) => openEnlarged(index, event.currentTarget)}
      className="absolute inset-0 z-[1] cursor-zoom-in"
    />
  );
  const mediumCount = (hasViewer ? 1 : 0) + (swatch || secondaryIndex !== null ? 1 : 0);

  const drawerItems: ViewerItem[] = [...(video ? ['video' as const] : []), ...viewerImages.map((_, index) => index)];
  const showDrawer = drawerItems.length > 1;
  const drawerVisible = drawerOpen ? drawerItems : drawerItems.slice(0, DRAWER_COLLAPSED);
  const hiddenCount = drawerItems.length - drawerVisible.length;
  const videoPoster = video?.poster ?? major;

  const viewerLabel = viewerIndex === null ? 'Video' : `${viewerIndex + 1} / ${viewerImages.length}`;

  // A lone medium tile spans the row as a 2:1 band rather than a square that dwarfs the hero.
  const mediumTileClassName = classnames(
    'relative overflow-hidden rounded-md',
    mediumCount === 1 ? 'col-span-2 aspect-[2/1]' : 'aspect-square',
  );

  const renderVideoTile = () =>
    video && (
      <div className={classnames(mediumTileClassName, 'bg-black')}>
        {videoPlaying && (video.src || video.youtubeId) ? (
          <VideoPlayer video={video} />
        ) : (
          <button
            type="button"
            onClick={() => setVideoPlaying(true)}
            className="group flex size-full items-center justify-center"
            aria-label={`Play video: ${video.title}`}
          >
            {videoPoster && (
              <Image
                src={videoPoster.src}
                alt=""
                media={videoPoster.media}
                cloudinaryPreset="gallery"
                // Without its own poster the tile falls back to the lifestyle photo, which needs the
                // same square crop as the major.
                ratio={!video.poster && lifestyle ? SQUARE : undefined}
                gravity={videoPoster.gravity}
                fill
                style={{ objectFit: 'cover', opacity: 0.8 }}
                sizes={MEDIUM_SIZES}
                loading="lazy"
                fetchPriority="low"
              />
            )}
            <span className="relative z-10">
              <WatchVideoPill duration={video.duration} />
            </span>
            <span className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/70 to-transparent p-12 pt-32 text-left text-14 font-medium text-white">
              {video.title}
            </span>
          </button>
        )}
      </div>
    );

  const renderImageTile = (image: ImageProps, viewerImageIndex: number, badge?: string) => (
    <div className={classnames(mediumTileClassName, 'border border-stroke-primary bg-white')}>
      {enlargeButton(lifestyleCount + viewerImageIndex)}
      <Image
        src={image.src}
        alt={image.alt}
        media={image.media}
        cloudinaryPreset="gallery"
        fill
        style={{ objectFit: 'cover' }}
        sizes={MEDIUM_SIZES}
        loading="lazy"
        fetchPriority="low"
      />
      {badge && (
        <span className="pointer-events-none absolute bottom-12 left-12 z-[2] rounded-full bg-white/90 px-12 py-4 text-12 font-medium text-primary-black">
          {badge}
        </span>
      )}
    </div>
  );

  return (
    // lg:h-full lets the pinned group below stick for the whole height of the PDP's gallery column.
    <div className="mx-auto flex w-full max-w-[740px] flex-col gap-12 lg:h-full">
      <div
        data-mosaic-major
        className="relative aspect-square w-full overflow-hidden bg-white md:rounded-md md:border md:border-stroke-primary"
      >
        {major && (
          <Image
            src={major.src}
            alt={major.alt}
            media={major.media}
            cloudinaryPreset="gallery"
            ratio={lifestyle ? SQUARE : undefined}
            gravity={major.gravity}
            fill
            style={{ objectFit: 'cover' }}
            sizes={MAJOR_SIZES}
            // Eager, but no fetchPriority="high": React copies it onto the image's HTTP Link preload
            // header, whose srcset Chrome can't parse (Cloudinary commas arrive as %2C), so it
            // fetches the oversized fallback URL at high priority and slows phone LCP ~100ms.
            loading="eager"
          />
        )}
        {/* Lifestyle photos sit first in the enlarged set; without one the major is viewer image 0. */}
        {major && enlargeButton(lifestyle ? Math.min(lifestyleIndex, lifestyleCount - 1) : lifestyleCount)}
        {renderLifestyleTray()}
      </div>

      {/* Reserves the swipe gallery's thumbnail strip below desktop (12px column gap + 100px here =
          its 8px margin + 104px strip), so the page doesn't jump when it takes over from this
          server-rendered markup. Measured at 375–1000px; keep in step with the swipe gallery. */}
      {swipeImages.length > 1 && <div className="h-100 lg:hidden" aria-hidden="true" />}

      {/* Medium tiles + drawer pin together under the header once they scroll up to it, and stay in
          view down the rest of the page while the info column scrolls past. */}
      <div
        data-mosaic-sticky
        className="hidden flex-col gap-12 lg:sticky lg:top-[calc(var(--header-height,0px)+var(--buy-bar-reserve,0px)+16px)] lg:flex"
      >
        {mediumCount > 0 && (
          <div data-mosaic-medium className="hidden grid-cols-2 gap-12 lg:grid">
            {/* Medium A — the viewer */}
            {viewerItem === 'video'
              ? renderVideoTile()
              : viewerImage && viewerIndex !== null && renderImageTile(viewerImage, viewerIndex, viewerLabel)}

            {/* Medium B — swatch, or the next image when the product has no finishes */}
            {swatch ? (
              <div
                className={classnames(mediumTileClassName, 'border border-stroke-primary')}
                style={swatch.image ? undefined : { backgroundColor: swatch.hex ?? undefined }}
              >
                {swatch.image && (
                  <Image
                    src={swatch.image.src}
                    alt={swatch.name ? `${swatch.name} finish` : ''}
                    media={swatch.image.media}
                    cloudinaryPreset="gallery"
                    fill
                    style={{ objectFit: 'cover' }}
                    sizes={MEDIUM_SIZES}
                    loading="lazy"
                    fetchPriority="low"
                  />
                )}
                {renderSwatchOverlay()}
              </div>
            ) : (
              secondaryIndex !== null &&
              viewerImages[secondaryIndex] &&
              renderImageTile(viewerImages[secondaryIndex], secondaryIndex)
            )}
          </div>
        )}

        {showDrawer && (
          <div className="hidden grid-cols-8 gap-8 lg:grid">
            {drawerVisible.map((item) => {
              const selected = item === viewerItem;
              const thumb = item === 'video' ? videoPoster : viewerImages[item];
              return (
                <button
                  key={item}
                  type="button"
                  onClick={() => {
                    setViewerItem(item);
                    if (item !== 'video') onActiveIndexChange?.(viewerToFull[item]);
                  }}
                  aria-label={
                    item === 'video'
                      ? `Show video: ${video?.title}`
                      : `Show image ${item + 1} of ${viewerImages.length}`
                  }
                  aria-pressed={selected}
                  className={classnames(
                    'relative aspect-square overflow-hidden rounded-sm border-2 bg-white',
                    selected ? 'border-black' : 'border-neutral-black-200',
                  )}
                >
                  {thumb && (
                    <Image
                      src={thumb.src}
                      alt={thumb.alt}
                      media={thumb.media}
                      cloudinaryPreset="thumbnail"
                      fill
                      style={{ objectFit: 'cover' }}
                      sizes={THUMB_SIZES}
                      loading="lazy"
                      fetchPriority="low"
                    />
                  )}
                  {item === 'video' && <PlayBadge />}
                </button>
              );
            })}
            {hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                aria-expanded={false}
                className="flex aspect-square items-center justify-center rounded-sm bg-black text-center text-14 font-medium leading-tight text-white"
              >
                +{hiddenCount}
                <br />
                more
              </button>
            )}
            {drawerOpen && drawerItems.length > DRAWER_COLLAPSED && (
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-expanded
                className="flex aspect-square items-center justify-center rounded-sm border-2 border-neutral-black-200 text-center text-14 font-medium leading-tight text-primary-black"
              >
                Show
                <br />
                less
              </button>
            )}
          </div>
        )}
      </div>

      {/* Mounted only while open: nothing to hydrate on page load. */}
      {enlarge.modalOpen && (
        <SliderModal
          isOpen={enlarge.modalOpen}
          onClose={enlarge.handleCloseModal}
          imageUrl={enlarge.activeImageUrl}
          imageAlt={enlarge.activeImageAlt}
          imageMedia={enlarge.activeImageMedia}
          images={enlarge.images}
          activeIndex={enlarge.activeIndex}
          totalImages={enlarge.totalImages}
          onNavigate={enlarge.goToImage}
          onPrevious={enlarge.goToPrevious}
          onNext={enlarge.goToNext}
          canGoNext={enlarge.canGoNext}
          canGoPrevious={enlarge.canGoPrevious}
        />
      )}
    </div>
  );
};

export default GalleryMosaic;
